// frontend/src/pages/FlowBuilderPage.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, FileText, Loader2, Search, Trash2, Save, FolderOpen, Play, Layout, X, ChevronLeft, ChevronRight, Eye, ArrowLeft, ArrowRight, Download, Upload, Link2 } from 'lucide-react';
import CreatePageModal from '@/components/builder/CreatePageModal';
import { getPageConfigs, PageConfig, saveJourneyConfig, savePageConfig, getJourneyConfigs, JourneyConfig, deleteJourneyConfig, getPageConfig } from '@/services/builderService';
import { toast } from 'sonner';
import Modal from '@/components/ui/Modal';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Connection,
  Edge,
  Node,
  ReactFlowProvider,
  Handle,
  Position,
  Panel
} from 'reactflow';
import 'reactflow/dist/style.css';
import { resolveRegistryEntry } from '@/registry/componentRegistry';

// Custom Node for the pages
const PageNode = ({ id, data, isConnectable }: any) => {
  return (
    <div className="bg-white border-2 border-intellend-green rounded-lg shadow-md w-56 flex flex-col group relative">
      <Handle 
        type="target" 
        position={Position.Left} 
        isConnectable={isConnectable} 
        className="w-3 h-3 bg-blue-500"
      />
      <div className="flex items-center p-3">
        <div className="bg-green-50 p-2 rounded mr-3 text-intellend-green flex-shrink-0">
          <FileText className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="truncate font-medium text-gray-800 text-sm" title={data.name}>
            {data.name}
          </div>
          <div className="text-xs text-gray-500 truncate">
            {data.appType || 'Custom'}
          </div>
        </div>
        <button 
          className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-opacity ml-1"
          onClick={() => data.onDelete(id)}
          title="Delete page from flow"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <Handle 
        type="source" 
        position={Position.Right} 
        isConnectable={isConnectable} 
        className="w-3 h-3 bg-intellend-green"
      />
    </div>
  );
};

const nodeTypes = {
  pageNode: PageNode,
};

// ─────────────────────────────────────────────────────────────────────────────
// Error boundary — catches render errors from individual preview components
// so one broken component doesn't block the entire preview modal.
// ─────────────────────────────────────────────────────────────────────────────
class PreviewErrorBoundary extends React.Component<
  { children: React.ReactNode; label: string },
  { hasError: boolean }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
          <p className="text-amber-700 text-xs font-medium">⚠ Preview unavailable</p>
          <p className="text-amber-500 text-xs mt-0.5">{this.props.label}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Flow Preview Modal
// Renders each page in the flow (in topological order) with its components
// displayed in builder/read-only mode. Users can step through pages with
// Prev / Next buttons.
//
// Key design decisions:
//  • isBuilderMode={true}  — prevents components from firing live API calls
//  • autoFire components   — shown as a placeholder card (they take over screen)
//  • PreviewErrorBoundary  — one crashing component won't block navigation
// ─────────────────────────────────────────────────────────────────────────────
interface FlowPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderedPageIds: number[];
}

function FlowPreviewModal({ isOpen, onClose, orderedPageIds }: FlowPreviewModalProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pageConfig, setPageConfig] = useState<PageConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Reset to first page whenever the modal opens
  useEffect(() => {
    if (isOpen && orderedPageIds.length > 0) {
      setCurrentIndex(0);
    }
  }, [isOpen, orderedPageIds]);

  // Fetch page config whenever the current index changes
  useEffect(() => {
    if (!isOpen || orderedPageIds.length === 0) return;
    const pageId = orderedPageIds[currentIndex];
    if (!pageId) return;

    setIsLoading(true);
    setPageConfig(null);
    getPageConfig(pageId)
      .then(config => setPageConfig(config))
      .catch(err => {
        console.error('FlowPreview: failed to load page', pageId, err);
        toast.error(`Failed to load page ${pageId}`);
        setIsLoading(false);
      })
      .finally(() => setIsLoading(false));
  }, [isOpen, currentIndex, orderedPageIds]);

  if (!isOpen) return null;

  const totalPages = orderedPageIds.length;
  const progress = totalPages > 0 ? ((currentIndex + 1) / totalPages) * 100 : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      {/* Phone mockup wrapper */}
      <div className="relative flex flex-col bg-white rounded-3xl overflow-hidden shadow-2xl" style={{ width: 390, maxHeight: '90vh' }}>
        {/* Header bar */}
        <div className="bg-gray-900 px-5 pt-4 pb-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-green-400" />
            <span className="text-white text-sm font-semibold">Flow Preview</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-gray-400 text-xs">
              Page {currentIndex + 1} of {totalPages}
            </span>
            <button
              onClick={onClose}
              className="p-1 rounded-full text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="bg-gray-800 h-1.5 flex-shrink-0">
          <div
            className="h-full bg-green-400 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Page name strip */}
        <div className="bg-gray-800 px-5 py-2 border-b border-gray-700 flex-shrink-0 flex items-center gap-2" style={{ minHeight: 32 }}>
          {pageConfig && (
            <>
              <p className="text-white text-xs font-medium truncate flex-1">{pageConfig.name}</p>
              {pageConfig.layout?.count_in_progress === false && (
                <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-600 text-gray-300 border border-gray-500">
                  👁 Observer
                </span>
              )}
            </>
          )}
        </div>

        {/* Scrollable content area — always white background */}
        <div className="bg-white flex-1 overflow-y-auto" style={{ minHeight: 300, maxHeight: 'calc(90vh - 200px)' }}>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              <p className="text-gray-400 text-sm">Loading page…</p>
            </div>
          ) : !pageConfig ? (
            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
              No page data available.
            </div>
          ) : pageConfig.components.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
              This page has no components.
            </div>
          ) : (
            <div className="p-4 space-y-3 bg-gray-50">
              {pageConfig.components.map((componentId, idx) => {
                const registryEntry = resolveRegistryEntry(componentId);
                if (!registryEntry) return null;

                const Component = registryEntry.component;
                // Use layout config as the value (read-only preview)
                const value = (pageConfig.layout && pageConfig.layout[componentId]) ??
                              registryEntry.metadata.validation?.defaultValues ?? {};

                return (
                  <PreviewErrorBoundary
                    key={`${componentId}-${idx}`}
                    label={registryEntry.metadata.name || componentId}
                  >
                    {/*
                      pointer-events-none + select-none: blocks all user interaction
                      (typing, clicking, focusing) so the preview is purely visual.
                      overflow-hidden on the outer div prevents components that use
                      min-h-screen from expanding beyond the modal's scroll area.
                      onDone / onChange are no-ops so autoFire components don't crash.
                    */}
                    <div className="rounded-xl overflow-hidden border border-gray-100 shadow-sm pointer-events-none select-none">
                      <Component
                        value={value}
                        onChange={() => { /* read-only preview */ }}
                        onDone={() => { /* no-op in preview */ }}
                        disabled={true}
                        isBuilderMode={true}
                      />
                    </div>
                  </PreviewErrorBoundary>
                );
              })}
            </div>
          )}
        </div>

        {/* Navigation footer */}
        <div className="bg-white border-t px-4 py-3 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={() => setCurrentIndex(i => Math.max(0, i - 1))}
            disabled={currentIndex === 0}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Prev
          </button>

          {/* Step dots */}
          <div className="flex-1 flex items-center justify-center gap-1.5 overflow-hidden">
            {orderedPageIds.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`rounded-full transition-all ${
                  i === currentIndex
                    ? 'w-5 h-2 bg-intellend-green'
                    : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          {currentIndex < totalPages - 1 ? (
            <button
              onClick={() => setCurrentIndex(i => Math.min(totalPages - 1, i + 1))}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-intellend-green text-white text-sm font-medium hover:bg-green-700 transition-colors"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gray-800 text-white text-sm font-medium hover:bg-gray-900 transition-colors"
            >
              Done
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Save Flow Modal — replaces the browser prompt() with a proper form that
// includes source URL binding fields (source_slug, entry_mode, bap_name).
// ─────────────────────────────────────────────────────────────────────────────
interface SaveFlowModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (values: {
    name: string;
    source_slug: string;
    entry_mode: 'FRESH' | 'PRE_APPROVED';
    bap_name: string;
  }) => void;
  isSaving: boolean;
  initial?: {
    name?: string;
    source_slug?: string | null;
    entry_mode?: 'FRESH' | 'PRE_APPROVED';
    bap_name?: string | null;
  };
}

function SaveFlowModal({ isOpen, onClose, onSave, isSaving, initial }: SaveFlowModalProps) {
  const [name, setName] = useState('');
  const [sourceSlug, setSourceSlug] = useState('');
  const [entryMode, setEntryMode] = useState<'FRESH' | 'PRE_APPROVED'>('FRESH');
  const [bapName, setBapName] = useState('');

  // Sync form values whenever the modal opens or initial values change
  useEffect(() => {
    if (isOpen) {
      setName(initial?.name || '');
      setSourceSlug(initial?.source_slug || '');
      setEntryMode(initial?.entry_mode || 'FRESH');
      setBapName(initial?.bap_name || '');
    }
  }, [isOpen, initial]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      source_slug: sourceSlug.trim(),
      entry_mode: entryMode,
      bap_name: bapName.trim(),
    });
  };

  // Auto-slugify the name into source_slug when user hasn't typed one yet
  const handleNameChange = (v: string) => {
    setName(v);
    if (!sourceSlug) {
      setSourceSlug(v.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''));
    }
  };

  const previewUrl = sourceSlug
    ? `/loan-application?source=${sourceSlug}`
    : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-intellend-green px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Save className="h-5 w-5 text-white" />
            <h2 className="text-white font-semibold text-lg">Save Flow</h2>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Journey Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Journey Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={e => handleNameChange(e.target.value)}
              placeholder="e.g. PhonePe Premium Journey"
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-intellend-green"
            />
          </div>

          {/* Divider */}
          <div className="border-t border-dashed border-gray-200 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Link2 className="h-4 w-4 text-intellend-green" />
              <span className="text-sm font-semibold text-gray-700">Source URL Binding</span>
              <span className="text-xs text-gray-400 font-normal">(optional)</span>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Bind this journey to a campaign URL. Users arriving at
              <code className="bg-gray-100 px-1 rounded mx-1">/loan-application?source=&lt;slug&gt;</code>
              will be routed to this journey automatically.
            </p>

            {/* Source Slug */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Source Slug
              </label>
              <input
                type="text"
                value={sourceSlug}
                onChange={e => setSourceSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                placeholder="e.g. phonepay_premium"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-intellend-green font-mono"
              />
              {previewUrl && (
                <p className="text-xs text-intellend-green mt-1 font-mono truncate">
                  🔗 {previewUrl}
                </p>
              )}
            </div>

            {/* Entry Mode */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Entry Mode
              </label>
              <div className="flex gap-3">
                {(['FRESH', 'PRE_APPROVED'] as const).map(mode => (
                  <label
                    key={mode}
                    className={`flex-1 flex items-start gap-2 border rounded-lg p-3 cursor-pointer transition-colors ${
                      entryMode === mode
                        ? 'border-intellend-green bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="entry_mode"
                      value={mode}
                      checked={entryMode === mode}
                      onChange={() => setEntryMode(mode)}
                      className="mt-0.5 accent-intellend-green"
                    />
                    <div>
                      <div className="text-xs font-semibold text-gray-700">{mode === 'FRESH' ? 'Fresh' : 'Pre-Approved'}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {mode === 'FRESH'
                          ? 'No existing records — created on-the-fly at OTP'
                          : 'Records already exist (uploaded via CSV)'}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* BAP Name */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                BAP Name
                <span className="text-gray-400 font-normal ml-1">(used when Entry Mode = Fresh)</span>
              </label>
              <input
                type="text"
                value={bapName}
                onChange={e => setBapName(e.target.value.toUpperCase())}
                placeholder="e.g. PHONEPE, PETPOOJA, NIYOGIN"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-intellend-green font-mono uppercase"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 rounded-lg py-2.5 text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || !name.trim()}
              className="flex-1 bg-intellend-green text-white rounded-lg py-2.5 text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {isSaving ? 'Saving…' : 'Save Flow'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Journey List Modal
// ─────────────────────────────────────────────────────────────────────────────
function JourneyListModal({ isOpen, onClose, onSelectJourney }: { isOpen: boolean, onClose: () => void, onSelectJourney: (journey: JourneyConfig) => void }) {
  const [journeys, setJourneys] = useState<JourneyConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteVisibleId, setDeleteVisibleId] = useState<number | null>(null);
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchJourneys = async () => {
    try {
      setLoading(true);
      const data = await getJourneyConfigs();
      setJourneys(data);
    } catch (err) {
      console.error("Failed to load journeys", err);
      toast.error("Failed to load saved flows");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchJourneys();
    }
  }, [isOpen]);

  const startPress = (id: number) => {
    pressTimer.current = setTimeout(() => {
      setDeleteVisibleId(id);
      toast.info("Delete option revealed");
    }, 800);
  };

  const cancelPress = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: number, name: string) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete the flow "${name}"?`)) {
      try {
        await deleteJourneyConfig(id);
        toast.success(`Flow "${name}" deleted.`);
        fetchJourneys();
        setDeleteVisibleId(null);
      } catch (err) {
        toast.error("Failed to delete flow.");
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Open Saved Flow">
      <div className="mt-4 space-y-2 max-h-[400px] overflow-y-auto pr-2">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin text-gray-400" />
          </div>
        ) : journeys.length === 0 ? (
          <p className="text-center py-8 text-gray-500 text-sm">No saved flows found.</p>
        ) : (
          journeys.map(j => (
            <div key={j.id} className="relative group">
              <button
                onMouseDown={() => startPress(j.id!)}
                onMouseUp={cancelPress}
                onMouseLeave={cancelPress}
                onTouchStart={() => startPress(j.id!)}
                onTouchEnd={cancelPress}
                onClick={() => onSelectJourney(j)}
                className={`w-full text-left p-3 border rounded-lg hover:border-intellend-green hover:bg-green-50 transition-colors flex items-center justify-between ${deleteVisibleId === j.id ? 'border-red-200 bg-red-50' : ''}`}
              >
                <div className="flex-1 mr-4">
                  <div className="font-medium text-gray-800">{j.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">Version {j.version} • {j.app_type}</div>
                  {j.source_slug && (
                    <div className="mt-1 flex items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono bg-green-50 text-intellend-green border border-green-200 rounded px-1.5 py-0.5">
                        🔗 ?source={j.source_slug}
                      </span>
                      <span className="text-[10px] text-gray-400">{j.entry_mode || 'FRESH'}</span>
                    </div>
                  )}
                </div>
                
                {deleteVisibleId === j.id ? (
                  <button
                    onClick={(e) => handleDelete(e, j.id!, j.name)}
                    className="p-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200 transition-colors flex items-center gap-1 text-xs font-bold"
                  >
                    <Trash2 className="h-3 w-3" />
                    DELETE
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity italic">Hold to delete</span>
                    <Play className="h-4 w-4 text-gray-300 group-hover:text-intellend-green opacity-0 group-hover:opacity-100 transition-all" />
                  </div>
                )}
              </button>
              
              {deleteVisibleId === j.id && (
                <button 
                  className="absolute -top-2 -right-2 bg-gray-500 text-white rounded-full p-0.5 shadow-sm"
                  onClick={(e) => { e.stopPropagation(); setDeleteVisibleId(null); }}
                  aria-label="Cancel delete"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Canvas
// ─────────────────────────────────────────────────────────────────────────────
function FlowBuilderCanvas() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isJourneyListOpen, setIsJourneyListOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [pages, setPages] = useState<PageConfig[]>([]);
  const [isLoadingPages, setIsLoadingPages] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentJourney, setCurrentJourney] = useState<JourneyConfig | null>(null);
  const [isSavingFlow, setIsSavingFlow] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  
  const navigate = useNavigate();

  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const importFileRef = useRef<HTMLInputElement>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);

  useEffect(() => {
    if (reactFlowInstance) {
      const timer = setTimeout(() => {
        reactFlowInstance.fitView({ duration: 300 });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isSidebarOpen, reactFlowInstance]);

  useEffect(() => {
    async function fetchPages() {
      try {
        const pageConfigs = await getPageConfigs();
        setPages(Array.isArray(pageConfigs) ? pageConfigs : []);
      } catch (err) {
        console.error("Failed to load pages:", err);
      } finally {
        setIsLoadingPages(false);
      }
    }
    fetchPages();
  }, []);

  const handleCreatePageClick = () => {
    setIsModalOpen(true);
  };

  /**
   * Derive the ordered page list by traversing the edge graph (topological sort).
   * Supports multi-branch flows: performs a BFS/DFS from all root nodes (nodes
   * with no incoming edges), following edges in order.
   */
  const getOrderedPageIds = useCallback((): number[] => {
    if (nodes.length === 0) return [];

    // Build adjacency map: nodeId → [next nodeId, ...]  (supports fan-out)
    const nextMap: Record<string, string[]> = {};
    const hasIncoming = new Set<string>();

    edges.forEach(e => {
      if (!nextMap[e.source]) nextMap[e.source] = [];
      nextMap[e.source].push(e.target);
      hasIncoming.add(e.target);
    });

    // Find root nodes (no incoming edges)
    const roots = nodes.filter(n => !hasIncoming.has(n.id));

    // DFS from each root, collecting pageIds in traversal order
    const visited = new Set<string>();
    const ordered: number[] = [];

    const traverse = (nodeId: string) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);
      const node = nodes.find(n => n.id === nodeId);
      if (node) ordered.push(node.data.pageId);
      const nexts = nextMap[nodeId] || [];
      nexts.forEach(next => traverse(next));
    };

    roots.forEach(r => traverse(r.id));

    // Append any disconnected nodes at the end
    nodes.forEach(n => {
      if (!visited.has(n.id)) ordered.push(n.data.pageId);
    });

    return ordered;
  }, [nodes, edges]);

  const handleSaveFlow = () => {
    if (nodes.length === 0) {
      toast.error("Flow is empty. Add some pages first.");
      return;
    }
    // Open the Save Flow modal instead of using prompt()
    setIsSaveModalOpen(true);
  };

  const handleSaveFlowConfirm = async (values: {
    name: string;
    source_slug: string;
    entry_mode: 'FRESH' | 'PRE_APPROVED';
    bap_name: string;
  }) => {
    setIsSavingFlow(true);
    try {
      const flowData = reactFlowInstance.toObject();
      const orderedPages = getOrderedPageIds();

      const journeyPayload: JourneyConfig = {
        id: currentJourney?.id,
        name: values.name,
        app_type: 'LOS',
        pages: orderedPages,
        flow_logic: {
          nodes: flowData.nodes,
          edges: flowData.edges,
        },
        is_active: true,
        version: (currentJourney?.version || 0) + 1,
        // Source URL binding — only include if slug was provided
        source_slug: values.source_slug || null,
        entry_mode: values.entry_mode,
        bap_name: values.bap_name || null,
      };

      const saved = await saveJourneyConfig(journeyPayload);
      setCurrentJourney(saved);
      setIsSaveModalOpen(false);

      const urlHint = values.source_slug
        ? ` → /loan-application?source=${values.source_slug}`
        : '';
      toast.success(`Flow "${values.name}" saved successfully.${urlHint}`);
    } catch (err) {
      console.error("Save flow failed", err);
      toast.error("Failed to save flow. Check console for details.");
    } finally {
      setIsSavingFlow(false);
    }
  };

  const handleExportFlow = async () => {
    if (nodes.length === 0) {
      toast.error("Flow is empty. Nothing to export.");
      return;
    }

    const flowData = reactFlowInstance ? reactFlowInstance.toObject() : { nodes, edges, viewport: {} };
    const orderedPages = getOrderedPageIds();

    // Fetch full PageConfig data for every page in the flow so the export is self-contained
    const pageDataMap: Record<number, PageConfig> = {};
    await Promise.all(
      orderedPages.map(async (pageId) => {
        try {
          const pc = await getPageConfig(pageId);
          pageDataMap[pageId] = pc;
        } catch {
          // If fetch fails, fall back to whatever is in the local pages list
          const local = pages.find(p => p.id === pageId);
          if (local) pageDataMap[pageId] = local;
        }
      })
    );

    const exportPayload = {
      name: currentJourney?.name || "Untitled Flow",
      app_type: currentJourney?.app_type || 'LOS',
      pages: orderedPages,
      // Full page definitions — components, layout, name, app_type — so they can be recreated on import
      page_data: orderedPages.map(id => pageDataMap[id]).filter(Boolean),
      flow_logic: {
        nodes: flowData.nodes,
        edges: flowData.edges,
      },
      is_active: currentJourney?.is_active ?? true,
      version: currentJourney?.version || 1,
      exported_at: new Date().toISOString(),
    };

    const json = JSON.stringify(exportPayload, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeName = (currentJourney?.name || 'flow').replace(/[^a-z0-9_\-]/gi, '_').toLowerCase();
    a.href = url;
    a.download = `${safeName}_flow.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Flow exported as "${a.download}" (${orderedPages.length} pages included).`);
  };

  const handleImportFlow = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!event.target) return;
    // Reset so the same file can be re-imported if needed
    event.target.value = '';
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        const data = JSON.parse(text);

        if (!data.flow_logic || !Array.isArray(data.flow_logic.nodes)) {
          toast.error("Invalid flow file: missing flow_logic.nodes.");
          return;
        }

        // ── Step 1: Check for name collision with existing saved journeys ────────
        let importName: string = data.name || file.name.replace(/\.json$/i, '');
        try {
          const existingJourneys = await getJourneyConfigs();
          const nameExists = existingJourneys.some(
            j => j.name.trim().toLowerCase() === importName.trim().toLowerCase()
          );
          if (nameExists) {
            const newName = prompt(
              `A journey named "${importName}" already exists.\nEnter a new name for the imported flow:`,
              `${importName} (imported)`
            );
            if (!newName) {
              toast.info("Import cancelled.");
              return;
            }
            importName = newName.trim() || importName;
          }
        } catch {
          // If we can't fetch journeys, proceed without the name check
        }

        // ── Step 2: Recreate missing pages from embedded page_data ───────────────
        // Build a map: old page ID → new page ID (may be the same if page already exists)
        const pageIdRemap: Record<number, number> = {};
        const embeddedPages: PageConfig[] = Array.isArray(data.page_data) ? data.page_data : [];

        if (embeddedPages.length > 0) {
          // Fetch current pages to check which ones already exist (by name)
          let currentPages: PageConfig[] = [];
          try { currentPages = await getPageConfigs(); } catch { /* ignore */ }

          for (const pd of embeddedPages) {
            const oldId = pd.id;
            // Check if a page with the same name already exists
            const existing = currentPages.find(
              p => p.name.trim().toLowerCase() === pd.name.trim().toLowerCase()
            );
            if (existing) {
              pageIdRemap[oldId] = existing.id;
            } else {
              // Create the page with its full definition (components + layout)
              try {
                const created = await savePageConfig({
                  name: pd.name,
                  app_type: pd.app_type,
                  components: pd.components || [],
                  layout: pd.layout || {},
                });
                pageIdRemap[oldId] = created.id;
                // Add to local pages list so the canvas sidebar reflects it
                setPages(prev => [...prev, created]);
              } catch (err) {
                console.error(`Failed to recreate page "${pd.name}"`, err);
                // Fall back: keep the old ID so the node still renders
                pageIdRemap[oldId] = oldId;
              }
            }
          }
        }

        // ── Step 3: Remap page IDs in nodes and update pages array ───────────────
        const remapPageId = (id: number): number => pageIdRemap[id] ?? id;

        const remappedNodes = data.flow_logic.nodes.map((n: any) => ({
          ...n,
          data: {
            ...n.data,
            pageId: n.data?.pageId != null ? remapPageId(Number(n.data.pageId)) : n.data?.pageId,
            onDelete: handleDeleteNode,
          },
        }));

        const remappedPages: number[] = (data.pages || []).map((id: any) => remapPageId(Number(id)));

        // ── Step 4: Restore canvas ────────────────────────────────────────────────
        setNodes(remappedNodes);
        setEdges(data.flow_logic.edges || []);

        const journeyPayload: JourneyConfig = {
          id: undefined,
          name: importName,
          app_type: data.app_type || 'LOS',
          pages: remappedPages,
          flow_logic: {
            nodes: remappedNodes,
            edges: data.flow_logic.edges || [],
          },
          is_active: data.is_active ?? true,
          version: data.version || 1,
        };

        // ── Step 5: Auto-save to database so it appears in "Open Saved Flow" ────
        let savedJourney = journeyPayload;
        try {
          savedJourney = await saveJourneyConfig(journeyPayload);
          setCurrentJourney(savedJourney);
        } catch (saveErr) {
          console.error("Failed to auto-save imported journey", saveErr);
          // Still load it on canvas even if save fails
          setCurrentJourney(journeyPayload);
        }

        setTimeout(() => reactFlowInstance?.fitView({ padding: 0.2, duration: 400 }), 100);
        const pagesCreated = Object.keys(pageIdRemap).length;
        toast.success(
          `Flow "${importName}" imported & saved successfully.` +
          (pagesCreated > 0 ? ` (${pagesCreated} page(s) restored)` : '')
        );
      } catch (err) {
        console.error("Import flow failed", err);
        toast.error("Failed to parse flow file. Make sure it is a valid JSON export.");
      }
    };
    reader.readAsText(file);
  };

  const handleCancel = () => {
    if (window.confirm("Discard all unsaved changes and clear canvas?")) {
      setNodes([]);
      setEdges([]);
      setCurrentJourney(null);
      toast.info("Canvas cleared.");
    }
  };

  /**
   * Auto Layout — Grid layout following connection order.
   *
   * Algorithm:
   * 1. Perform a topological BFS from root nodes to get the ordered sequence.
   * 2. Place nodes in a grid: COLS_PER_ROW columns wide, snaking left-to-right
   *    row by row (like reading order).
   * 3. Convert all edges to 'smoothstep' so connections route orthogonally
   *    (straight horizontal/vertical segments, no diagonal crossing wires).
   *
   * Result: a clean grid where each node's position reflects its order in the
   * journey and all connecting lines are straight (no diagonal cross-wires).
   */
  const handleAutoLayout = () => {
    if (nodes.length === 0 || !reactFlowInstance) return;

    const COLS_PER_ROW = 4;   // number of columns in the grid
    const nodeWidth  = 224;   // w-56 in Tailwind
    const nodeHeight = 72;    // approximate rendered height
    const hGap = 80;          // horizontal gap between columns
    const vGap = 60;          // vertical gap between rows
    const padding = 60;

    // ── Step 1: topological BFS to get ordered node sequence ─────────────────
    const nextMap: Record<string, string[]> = {};
    const prevMap: Record<string, string[]> = {};

    edges.forEach(e => {
      if (!nextMap[e.source]) nextMap[e.source] = [];
      nextMap[e.source].push(e.target);
      if (!prevMap[e.target]) prevMap[e.target] = [];
      prevMap[e.target].push(e.source);
    });

    const roots = nodes.filter(n => !prevMap[n.id] || prevMap[n.id].length === 0);
    const startNodes = roots.length > 0 ? roots : [nodes[0]];

    const visited = new Set<string>();
    const orderedIds: string[] = [];

    const bfsQueue: string[] = startNodes.map(n => n.id);
    startNodes.forEach(n => visited.add(n.id));

    let head = 0;
    while (head < bfsQueue.length) {
      const nodeId = bfsQueue[head++];
      orderedIds.push(nodeId);
      (nextMap[nodeId] || []).forEach(nextId => {
        if (!visited.has(nextId)) {
          visited.add(nextId);
          bfsQueue.push(nextId);
        }
      });
    }

    // Append any disconnected nodes at the end (in their original order)
    nodes.forEach(n => {
      if (!visited.has(n.id)) orderedIds.push(n.id);
    });

    // ── Step 2: assign grid positions (reading order: left→right, top→bottom) ─
    const posMap: Record<string, { x: number; y: number }> = {};
    orderedIds.forEach((nodeId, idx) => {
      const col = idx % COLS_PER_ROW;
      const row = Math.floor(idx / COLS_PER_ROW);
      posMap[nodeId] = {
        x: padding + col * (nodeWidth + hGap),
        y: padding + row * (nodeHeight + vGap),
      };
    });

    const newNodes = nodes.map(node => ({
      ...node,
      position: posMap[node.id] ?? node.position,
    }));

    // ── Step 3: convert all edges to smoothstep (straight orthogonal routing) ─
    const newEdges = edges.map(edge => ({
      ...edge,
      type: 'smoothstep',
    }));

    setNodes(newNodes);
    setEdges(newEdges);
    setTimeout(() => reactFlowInstance?.fitView({ padding: 0.2, duration: 400 }), 50);
    toast.success('Auto-layout applied — grid arrangement with straight connections.');
  };

  const handleSelectJourney = (journey: JourneyConfig) => {
    setCurrentJourney(journey);
    
    if (journey.flow_logic && journey.flow_logic.nodes && journey.flow_logic.nodes.length > 0) {
      const hydratedNodes = journey.flow_logic.nodes.map((n: any) => ({
        ...n,
        data: {
          ...n.data,
          onDelete: handleDeleteNode
        }
      }));
      setNodes(hydratedNodes);
      setEdges(journey.flow_logic.edges || []);
    } else if (journey.pages && journey.pages.length > 0) {
      const newNodes: Node[] = [];
      const newEdges: Edge[] = [];
      
      journey.pages.forEach((pageId: any, index: number) => {
        const pageDetail = pages.find(p => p.id === Number(pageId));
        const nodeId = `node_auto_${pageId}_${index}`;
        
        newNodes.push({
          id: nodeId,
          type: 'pageNode',
          position: { x: index * 300 + 100, y: 100 },
          data: { 
            name: pageDetail?.name || `Page ${pageId}`, 
            pageId: Number(pageId), 
            appType: pageDetail?.app_type || 'LOS',
            onDelete: handleDeleteNode 
          },
        });

        if (index > 0) {
          const prevNodeId = `node_auto_${journey.pages[index-1]}_${index-1}`;
          newEdges.push({
            id: `edge_auto_${index}`,
            source: prevNodeId,
            target: nodeId,
            type: 'smoothstep',
          });
        }
      });
      
      setNodes(newNodes);
      setEdges(newEdges);
    }

    setIsJourneyListOpen(false);
    setTimeout(() => reactFlowInstance?.fitView(), 200);
    toast.success(`Loaded flow: ${journey.name}`);
  };

  const filteredPages = pages.filter(page => 
    page.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const onConnect = useCallback(
    (params: Edge | Connection) => setEdges((eds) => addEdge({ ...params, type: 'smoothstep' }, eds)),
    [setEdges]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDeleteNode = useCallback((nodeId: string) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
  }, [setNodes, setEdges]);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const dataString = event.dataTransfer.getData('application/json');
      if (!dataString || !reactFlowInstance) return;

      try {
        const data = JSON.parse(dataString);
        if (data.type === 'page') {
          const position = reactFlowInstance.screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
          });

          const newNode: Node = {
            id: `node_${Date.now()}`,
            type: 'pageNode',
            position,
            data: { 
              name: data.name, 
              pageId: data.id, 
              appType: data.appType,
              onDelete: handleDeleteNode 
            },
          };

          setNodes((nds) => nds.concat(newNode));
        }
      } catch (err) {
        console.error("Failed to parse drop data", err);
      }
    },
    [reactFlowInstance, setNodes, handleDeleteNode]
  );

  // Ordered page IDs for the preview modal (computed on demand)
  const previewPageIds = getOrderedPageIds();

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden relative">
      {/* Left Sidebar */}
      <aside className={`bg-white border-r shadow-xl flex flex-col z-30 transition-all duration-300 ease-in-out overflow-hidden ${isSidebarOpen ? 'w-80' : 'w-0'}`}>
        <div className="w-80 h-full flex flex-col">
          <div className="p-4 border-b bg-gray-50 flex flex-col gap-4">
            <button 
              onClick={handleCreatePageClick}
              className="w-full flex items-center justify-center py-2.5 px-4 bg-intellend-green text-white rounded-md shadow-sm text-sm font-medium hover:bg-green-700 transition-colors"
            >
              <PlusCircle className="h-5 w-5 mr-2" />
              Create New Page
            </button>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search existing pages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-intellend-green"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
            {isLoadingPages ? (
              <div className="flex justify-center items-center h-20">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : filteredPages.length === 0 ? (
              <div className="text-center text-sm text-gray-500 mt-6">
                {searchQuery ? "No pages match your search." : "No pages exist yet."}
              </div>
            ) : (
              filteredPages.map(page => (
                <div
                  key={page.id}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('application/json', JSON.stringify({
                      type: 'page',
                      id: page.id,
                      name: page.name,
                      appType: page.app_type
                    }));
                  }}
                  className="p-3 border border-gray-200 rounded-md bg-white shadow-sm hover:shadow-md hover:border-intellend-green cursor-grab active:cursor-grabbing transition-all flex items-center group"
                >
                  <div className="bg-blue-50 p-2 rounded mr-3 text-blue-600 group-hover:bg-blue-100 transition-colors flex-shrink-0">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm text-gray-800 truncate">{page.name}</h3>
                    <p className="text-xs text-gray-500 mt-1">{page.app_type || 'Custom'} App</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </aside>

      {/* Sidebar Toggle Button */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className={`absolute top-1/2 -translate-y-1/2 z-40 bg-white border border-gray-200 rounded-r-md p-1.5 shadow-md hover:bg-gray-50 transition-all duration-300 ${isSidebarOpen ? 'left-80' : 'left-0'}`}
        title={isSidebarOpen ? "Collapse Panel" : "Expand Panel"}
      >
        {isSidebarOpen ? <ChevronLeft className="h-5 w-5 text-gray-600" /> : <ChevronRight className="h-5 w-5 text-gray-600" />}
      </button>

      {/* Main Canvas Area */}
      <main className="flex-1 relative bg-gray-50" ref={reactFlowWrapper}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={setReactFlowInstance}
          onDrop={onDrop}
          onDragOver={onDragOver}
          nodeTypes={nodeTypes}
          fitView
        >
          <Panel position="top-right" className="flex gap-2 flex-wrap">
            {/* Hidden file input for Import */}
            <input
              ref={importFileRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={handleImportFlow}
            />
            <button
              onClick={() => setIsJourneyListOpen(true)}
              className="flex items-center px-4 py-2 bg-white border border-gray-200 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <FolderOpen className="h-4 w-4 mr-2 text-blue-500" />
              Open Flow
            </button>
            <button
              onClick={handleAutoLayout}
              className="flex items-center px-4 py-2 bg-white border border-gray-200 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Layout className="h-4 w-4 mr-2 text-purple-500" />
              Auto Layout
            </button>
            <button
              onClick={handleExportFlow}
              className="flex items-center px-4 py-2 bg-white border border-gray-200 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Download className="h-4 w-4 mr-2 text-orange-500" />
              Export Flow
            </button>
            <button
              onClick={() => importFileRef.current?.click()}
              className="flex items-center px-4 py-2 bg-white border border-gray-200 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Upload className="h-4 w-4 mr-2 text-indigo-500" />
              Import Flow
            </button>
            <button
              onClick={() => {
                if (nodes.length === 0) {
                  toast.error("No pages on canvas to preview.");
                  return;
                }
                setIsPreviewOpen(true);
              }}
              className="flex items-center px-4 py-2 bg-white border border-gray-200 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Eye className="h-4 w-4 mr-2 text-green-500" />
              Preview Flow
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center px-4 py-2 bg-white border border-gray-200 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveFlow}
              disabled={isSavingFlow}
              className="flex items-center px-4 py-2 bg-intellend-green text-white rounded-md shadow-sm text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {isSavingFlow ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {currentJourney ? 'Update Flow' : 'Save Flow'}
            </button>
          </Panel>
          <Background color="#cbd5e1" gap={20} size={1} />
          <Controls />
          <MiniMap nodeStrokeWidth={3} />
        </ReactFlow>

        {nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <div className="bg-white p-8 rounded-xl shadow-lg flex flex-col items-center pointer-events-auto border border-gray-100">
              <div className="bg-green-50 p-4 rounded-full mb-4">
                <FileText className="h-8 w-8 text-intellend-green" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Build Your Flow</h2>
              <p className="text-gray-500 mb-6 max-w-sm text-center">
                Drag pages from the left sidebar and drop them here. Connect them using the dots on the sides to define the user journey.
              </p>
              <button
                onClick={() => setIsJourneyListOpen(true)}
                className="text-intellend-green font-medium hover:underline pointer-events-auto"
              >
                Or open a saved flow
              </button>
            </div>
          </div>
        )}
      </main>

      <CreatePageModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <JourneyListModal
        isOpen={isJourneyListOpen}
        onClose={() => setIsJourneyListOpen(false)}
        onSelectJourney={handleSelectJourney}
      />
      <FlowPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        orderedPageIds={previewPageIds}
      />
      {/* Save Flow Modal — includes source URL binding fields */}
      <SaveFlowModal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onSave={handleSaveFlowConfirm}
        isSaving={isSavingFlow}
        initial={{
          name: currentJourney?.name,
          source_slug: currentJourney?.source_slug,
          entry_mode: currentJourney?.entry_mode,
          bap_name: currentJourney?.bap_name,
        }}
      />
    </div>
  );
}

export default function FlowBuilderPage() {
  return (
    <ReactFlowProvider>
      <FlowBuilderCanvas />
    </ReactFlowProvider>
  );
}
